import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, ArrowLeft, Sparkles, ChefHat } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STEPS = [
  { to: "/app", id: "app", title: "Your daily dashboard", body: "Today's compliance score, anything that needs attention, the latest inspector rating and a side-by-side view of every venue." },
  { to: "/app/checks", id: "checks", title: "Today's checks", body: "Opening, closing and cleaning checklists staff tick off on their phone, with photo proof and an offline mode for no-signal kitchens." },
  { to: "/app/temps", id: "temps", title: "Temperatures", body: "Fridge, freezer, cooking and hot-holding readings. Out-of-range readings alert managers instantly and record the corrective action." },
  { to: "/app/prep", id: "prep", title: "Prep & labels", body: "Log each batch you prep and print use-by and allergen labels with your logo. Tap Reprint label on any card to see one, or use Recall lookup to trace an ingredient." },
  { to: "/app/allergens", id: "allergens", title: "Allergens & QR menu", body: "The 14-allergen matrix for every dish, with 'may contain' warnings. Customers scan a QR code to see it live. Open the customer menu link to see what they see." },
  { to: "/app/records", id: "records", title: "Records & calendar", body: "Search every completed record, see each day colour-coded, explain missed checks and send inspectors a secure 7-day link." },
  { to: "/app/haccp", id: "haccp", title: "HACCP plans", body: "Build your HACCP plan with AI help, based on UK food-safety rules, and keep it versioned and ready for inspection." },
  { to: "/app/audit-pack", id: "audit-pack", title: "EHO audit pack", body: "One tap gives you a print-ready pack of checks, temperatures, incidents and training for your next EHO visit." },
  { to: "/app/team", id: "team", title: "Team & roles", body: "Invite managers, chefs and staff. Staff log records with a quick PIN, owners and managers run the business hub." },
];
const TOUR_KEY = "ka_demo_tour_done";
const START_KEY = "ka_demo_started_at";
const PROMPT_KEY = "ka_demo_prompt_shown";
const PROMPT_AFTER_MS = 3 * 60 * 1000;

export const restartDemoTour = () => window.dispatchEvent(new Event("ka-demo-tour"));

function useTarget(id, step) {
  const [rect, setRect] = useState(null);
  useLayoutEffect(() => {
    const measure = () => {
      const el = [...document.querySelectorAll(`[data-testid="nav-${id}-link"]`)].find((e) => e.offsetParent);
      setRect(el ? el.getBoundingClientRect() : null);
    };
    const el = [...document.querySelectorAll(`[data-testid="nav-${id}-link"]`)].find((e) => e.offsetParent);
    if (el) el.scrollIntoView({ block: "nearest" });
    measure();
    const raf = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [id, step]);
  return rect;
}

function TourCard({ step, setStep, close }) {
  const s = STEPS[step];
  const rect = useTarget(s.id, step);
  const last = step === STEPS.length - 1;
  const pos = rect ? { top: Math.max(16, Math.min(rect.top - 20, window.innerHeight - 260)), left: rect.right + 18 } : null;
  return (
    <div className="no-print fixed inset-0 z-[60]" data-testid="demo-tour">
      <div className="absolute inset-0 bg-slate-950/40" onClick={close} />
      {rect && <div className="pointer-events-none absolute rounded-lg ring-2 ring-emerald-400 shadow-[0_0_0_4px_rgba(16,185,129,0.25)] transition-[top,left] duration-300"
        style={{ top: rect.top - 3, left: rect.left - 3, width: rect.width + 6, height: rect.height + 6 }} />}
      <div data-testid="demo-tour-card" key={step}
        className={`ka-fade absolute w-[min(22rem,calc(100vw-2rem))] rounded-2xl bg-white p-5 shadow-2xl ${pos ? "" : "inset-x-0 bottom-24 mx-auto"}`}
        style={pos || undefined}>
        {pos && <div className="absolute -left-2 h-4 w-4 rotate-45 bg-white" style={{ top: Math.max(12, rect.top + rect.height / 2 - pos.top - 8) }} />}
        <div className="flex items-center justify-between">
          <span data-testid="demo-tour-progress" className="font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-700">Step {step + 1} of {STEPS.length}</span>
          <button data-testid="demo-tour-close" onClick={close} className="ka-press rounded p-1 text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>
        <h3 data-testid="demo-tour-title" className="mt-2 font-display text-lg font-extrabold text-slate-900">{s.title}</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{s.body}</p>
        <div className="mt-4 flex items-center gap-2">
          <button data-testid="demo-tour-skip" onClick={close} className="text-sm text-slate-500 hover:text-slate-800">Skip tour</button>
          {step > 0 && <button data-testid="demo-tour-back" onClick={() => setStep(step - 1)} className="ka-press ml-auto rounded-full border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"><ArrowLeft className="h-4 w-4" /></button>}
          <button data-testid="demo-tour-next" onClick={() => (last ? close() : setStep(step + 1))}
            className={`ka-press flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 ${step > 0 ? "" : "ml-auto"}`}>
            {last ? "Finish" : "Next"} {!last && <ArrowRight className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DemoTour() {
  const { business } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(null);
  const demo = !!business?.is_demo;
  useEffect(() => {
    if (demo && !localStorage.getItem(TOUR_KEY)) setStep(0);
    const open = () => setStep(0);
    window.addEventListener("ka-demo-tour", open);
    return () => window.removeEventListener("ka-demo-tour", open);
  }, [demo]);
  useEffect(() => {
    if (step !== null) nav(STEPS[step].to);
  }, [step, nav]);
  const close = useCallback(() => {
    localStorage.setItem(TOUR_KEY, "1");
    setStep(null);
  }, []);
  if (!demo || step === null) return null;
  return <TourCard step={step} setStep={setStep} close={close} />;
}

export function useStartOwnAccount() {
  const { logout } = useAuth();
  return async (businessName = "", venueName = "") => {
    await logout();
    const q = new URLSearchParams({ from: "demo" });
    if (businessName.trim()) q.set("business", businessName.trim().slice(0, 80));
    if (venueName.trim()) q.set("venue", venueName.trim().slice(0, 80));
    window.location.assign(`/signup?${q}`);
  };
}

export function DemoHint({ label = "Try this", tip, testid, inline }) {
  const { business } = useAuth();
  if (!business?.is_demo) return null;
  return (
    <span data-testid={testid} title={tip}
      className={`pointer-events-none z-10 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-950 shadow-sm ${inline ? "ml-auto" : "absolute -right-2 -top-2.5"}`}>
      <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-900/60" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-900" /></span>
      {label}
    </span>
  );
}

export function DemoSignupPrompt() {
  const { business } = useAuth();
  const startOwn = useStartOwnAccount();
  const [show, setShow] = useState(false);
  const [bizName, setBizName] = useState("");
  const [venueName, setVenueName] = useState("");
  const demo = !!business?.is_demo;
  useEffect(() => {
    if (!demo || sessionStorage.getItem(PROMPT_KEY)) return undefined;
    const started = Number(sessionStorage.getItem(START_KEY)) || Date.now();
    sessionStorage.setItem(START_KEY, String(started));
    const t = setTimeout(() => {
      sessionStorage.setItem(PROMPT_KEY, "1");
      setShow(true);
    }, Math.max(0, started + PROMPT_AFTER_MS - Date.now()));
    return () => clearTimeout(t);
  }, [demo]);
  if (!demo || !show) return null;
  return (
    <div className="no-print fixed inset-0 z-[70] flex items-end justify-center bg-slate-950/50 p-4 sm:items-center" data-testid="demo-signup-prompt">
      <div className="ka-fade relative w-full max-w-md overflow-hidden rounded-3xl bg-white p-7 shadow-2xl">
        <button data-testid="demo-signup-prompt-close" onClick={() => setShow(false)} className="ka-press absolute right-4 top-4 rounded p-1 text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700"><ChefHat className="h-6 w-6" /></div>
        <h2 className="mt-4 font-display text-2xl font-extrabold text-slate-900">Liking what you see?</h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">Set up your own kitchen in a few minutes. Your free trial includes everything in this demo, plus AI HACCP plans and your own private team app.</p>
        <form onSubmit={(e) => { e.preventDefault(); startOwn(bizName, venueName); }}>
        <label className="mt-5 block text-sm font-semibold text-slate-800" htmlFor="demo-biz-name">What's your business called?</label>
        <input id="demo-biz-name" data-testid="demo-signup-prompt-business-input" value={bizName} onChange={(e) => setBizName(e.target.value)} maxLength={80}
          placeholder="e.g. The Red Lion" className="ka-input mt-1.5 w-full" />
        <label className="mt-3 block text-sm font-semibold text-slate-800" htmlFor="demo-venue-name">And your first venue?</label>
        <input id="demo-venue-name" data-testid="demo-signup-prompt-venue-input" value={venueName} onChange={(e) => setVenueName(e.target.value)} maxLength={80}
          placeholder="e.g. High Street kitchen" className="ka-input mt-1.5 w-full" />
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <button type="submit" data-testid="demo-signup-prompt-start"
            className="ka-press flex flex-1 items-center justify-center gap-2 rounded-full bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-700">
            <Sparkles className="h-4 w-4" /> Start my free trial
          </button>
          <button type="button" data-testid="demo-signup-prompt-keep" onClick={() => setShow(false)}
            className="ka-press rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">Keep exploring</button>
        </div>
        </form>
      </div>
    </div>
  );
}
